import { useEffect, useState } from "react";
import * as uuid from 'uuid';
import Loader from "../UI/Loader";
import StructuredEditField from "./StructuredEditField";
import Button from "../UI/Button";
import StructuredFieldCollection from "../../models/pageModels/StructuredFieldCollection";

interface RepeatingStructuredEditFieldProps {
    id: string;
    label: string;
    addButtonText: string;
    name: string;
    value: string;
    disabled: boolean;
    columnNumber?: number;
    onChange: (value: string) => void;
    schema: { fieldTypeId: number, fieldLabel: string, fieldName: string, options?: { label: string; value: string; order: number }[], mask?: string }[]
}

const RepeatingStructuredEditField = (props: RepeatingStructuredEditFieldProps) => {
    const [fieldCollections, setFieldCollections] = useState<StructuredFieldCollection[]>();

    useEffect(() => {
        if (!props.value) {
            return;
        }

        const valueJson = JSON.parse(props.value);
        setFieldCollections(valueJson)
    }, [props.value]);

    const onFieldChange = (collectionId: string, fieldId: string, value: string) => {
        if (!fieldCollections) {
            return;
        }

        const matchingCollection = fieldCollections.find(fc => fc.id === collectionId);
        if (!matchingCollection) {
            console.warn('matchingCollection not found for id', collectionId);
            return;
        }

        const matchingField = matchingCollection.fields.find(f => f.id === fieldId);
        if (!matchingField) {
            console.warn('matchingField not found for id', fieldId, 'in collection', collectionId);
            return;
        }

        matchingField.value = value;

        props.onChange(JSON.stringify(fieldCollections));
    }

    const getDefaultFields = () => {
        return props.schema.map(s => {
            return {
                id: uuid.v4(),
                fieldTypeId: s.fieldTypeId,
                name: s.fieldName,
                value: '',
                label: s.fieldLabel,
                required: false,
                disabled: props.disabled,
                options: s.options,
                mask: s.mask
            }
        })
    }

    const handleOnAdd = () => {
        if (props.disabled) {
            return;
        }

        setFieldCollections([
            ...(fieldCollections || []),
            {
                id: uuid.v4(),
                fields: getDefaultFields()
            }
        ]);
    };

    const handleOnRemove = (id: string) => {
        if (props.disabled) {
            return;
        }

        setFieldCollections([
            ...(fieldCollections || []).filter(f => f.id !== id)
        ]);
    };

    if (!fieldCollections) {
        return <Loader />;
    }

    const columnClass = !props.columnNumber || props.columnNumber <= 0 || props.columnNumber > 12 ? "column-medium-4" : `column-medium-${Math.trunc(12 / props.columnNumber)}`;

    return <>
        <div className="row margin-bottom-5">
            <div className="column">
                <label>{props.label} ({fieldCollections.length})</label>
            </div>
        </div>
        {fieldCollections.map((fc, i) => {
            return <div className="row border padding-5 margin-top-5" key={fc.id}>
                <div className="column">
                    <div className="row">
                        {fc.fields.map(f => {
                            return <StructuredEditField
                                id={f.id}
                                key={f.id}
                                disabled={f.disabled}
                                columnClass={columnClass}
                                fieldTypeId={f.fieldTypeId}
                                label={f.label}
                                name={f.name}
                                options={f.options}
                                mask={f.mask}
                                onChange={(v) => onFieldChange(fc.id, f.id, v)}
                                required={f.required}
                                value={f.value} />
                        })}
                    </div>
                    <div className="row justify-content-end">
                        <div className="column-auto">
                            <Button id="remove-collection" 
                                onClick={() => handleOnRemove(fc.id)} 
                                className="warning"
                                disabled={props.disabled}
                                text="Remove" />
                        </div>
                    </div>
                </div>
            </div>
        })}
        <div className="row margin-top-5">
            <div className="column-auto">
                <Button id="add-collection" 
                    onClick={handleOnAdd} 
                    disabled={props.disabled}
                    text={props.addButtonText} />
            </div>
        </div>
    </>;
};

export default RepeatingStructuredEditField;